import { Button } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import { DIAS_SEMANA, type VentanaHoraria } from '../../../data/repositories/RepositorioUmbrales';
import estilos from './Umbrales.module.css';

interface EditorHorariosProps {
  horarios: readonly VentanaHoraria[];
  onCambiar: (horarios: VentanaHoraria[]) => void;
}

const VENTANA_NUEVA: VentanaHoraria = {
  horaInicio: '08:00',
  horaFin: '18:00',
  dias: [1, 2, 3, 4, 5],
};

/**
 * Ventanas horarias en las que el umbral vigila.
 *
 * Sirve para casos como «la puerta debe estar cerrada, pero sólo fuera del
 * horario laboral». Sin ninguna ventana el umbral vigila siempre, que es lo
 * que espera la mayoría y por eso es el estado inicial.
 */
export function EditorHorarios({ horarios, onCambiar }: EditorHorariosProps) {
  const actualizar = (indice: number, cambios: Partial<VentanaHoraria>) => {
    onCambiar(horarios.map((h, i) => (i === indice ? { ...h, ...cambios } : h)));
  };

  const alternarDia = (indice: number, dia: number) => {
    const ventana = horarios[indice];
    if (!ventana) return;

    const dias = ventana.dias.includes(dia)
      ? ventana.dias.filter((d) => d !== dia)
      : [...ventana.dias, dia].sort((a, b) => a - b);

    actualizar(indice, { dias });
  };

  return (
    <div className={estilos.seccionAnexa}>
      <div className={estilos.seccionCabecera}>
        <div>
          <strong>Horario de vigilancia</strong>
          <span className={estilos.opcionAyuda}>
            {horarios.length === 0
              ? 'Ahora mismo vigila las 24 horas, todos los días.'
              : 'Sólo se avisa dentro de estas franjas.'}
          </span>
        </div>
        <Button
          tamano="sm"
          variante="secundario"
          icono="agregar"
          onClick={() => onCambiar([...horarios, { ...VENTANA_NUEVA }])}
        >
          Añadir franja
        </Button>
      </div>

      {horarios.map((ventana, indice) => (
        <div key={ventana.id ?? indice} className={estilos.ventana}>
          <div className={estilos.ventanaHoras}>
            <label className={estilos.campoHora}>
              <span>Desde</span>
              <input
                type="time"
                value={ventana.horaInicio}
                onChange={(e) => actualizar(indice, { horaInicio: e.target.value })}
              />
            </label>
            <label className={estilos.campoHora}>
              <span>Hasta</span>
              <input
                type="time"
                value={ventana.horaFin}
                onChange={(e) => actualizar(indice, { horaFin: e.target.value })}
              />
            </label>

            <Button
              tamano="sm"
              variante="fantasma"
              icono="eliminar"
              title="Quitar franja"
              onClick={() => onCambiar(horarios.filter((_, i) => i !== indice))}
            />
          </div>

          <div className={estilos.dias}>
            {DIAS_SEMANA.map((nombre, dia) => {
              const activo = ventana.dias.includes(dia);
              return (
                <button
                  key={dia}
                  type="button"
                  className={[estilos.dia, activo ? estilos.diaActivo : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => alternarDia(indice, dia)}
                  aria-pressed={activo}
                  title={nombre}
                >
                  {nombre.slice(0, 1)}
                </button>
              );
            })}
          </div>

          {ventana.dias.length === 0 && (
            <span className={estilos.avisoMenor}>
              <Icon nombre="advertenciaContorno" tamano={13} />
              Sin días marcados esta franja no se aplica nunca.
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
